import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const automationRulesService = {
  async getAllRules(filters = {}) {
    try {
      let query = supabase
        ?.from('automation_rules')
        ?.select(`
          *,
          creator:user_profiles!automation_rules_created_by_fkey(
            id,
            full_name,
            email
          ),
          email_template:email_templates(
            id,
            template_name,
            segment
          ),
          conditions:automation_conditions(
            id,
            condition_group,
            field_name,
            operator,
            value_text,
            value_number,
            value_date,
            value_boolean,
            value_array,
            logical_operator,
            sort_order
          )
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.triggerType) {
        query = query?.eq('trigger_type', filters?.triggerType);
      }

      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching automation rules:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch automation rules:', error);
      throw error;
    }
  },

  async getRuleById(ruleId) {
    try {
      const { data, error } = await supabase
        ?.from('automation_rules')
        ?.select(`
          *,
          creator:user_profiles!automation_rules_created_by_fkey(
            id,
            full_name,
            email
          ),
          email_template:email_templates(
            id,
            template_name,
            segment,
            subject_line
          ),
          conditions:automation_conditions(
            id,
            condition_group,
            field_name,
            operator,
            value_text,
            value_number,
            value_date,
            value_boolean,
            value_array,
            logical_operator,
            sort_order
          )
        `)
        ?.eq('id', ruleId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching rule:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch rule:', error);
      throw error;
    }
  },

  async createRule(ruleData, conditions = []) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const snakeData = toSnakeCase({
        ...ruleData,
        createdBy: user?.id
      });

      const { data: rule, error: ruleError } = await supabase
        ?.from('automation_rules')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (ruleError) {
        if (isSchemaError(ruleError)) {
          console.error('Schema error:', ruleError?.message);
          throw ruleError;
        }
        console.log('Error creating rule:', ruleError?.message);
        return null;
      }

      // Insert conditions if provided
      if (conditions?.length > 0) {
        const conditionsWithRuleId = conditions?.map(cond => ({
          ...cond,
          ruleId: rule?.id
        }));
        await this.updateConditions(rule?.id, conditionsWithRuleId);
      }

      return toCamelCase(rule);
    } catch (error) {
      console.error('Failed to create rule:', error);
      throw error;
    }
  },

  async updateRule(ruleId, ruleData) {
    try {
      const snakeData = toSnakeCase(ruleData);

      const { data, error } = await supabase
        ?.from('automation_rules')
        ?.update(snakeData)
        ?.eq('id', ruleId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error updating rule:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update rule:', error);
      throw error;
    }
  },

  async deleteRule(ruleId) {
    try {
      const { error } = await supabase
        ?.from('automation_rules')
        ?.delete()
        ?.eq('id', ruleId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error deleting rule:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete rule:', error);
      throw error;
    }
  },

  async updateConditions(ruleId, conditions) {
    try {
      // Delete existing conditions
      await supabase
        ?.from('automation_conditions')
        ?.delete()
        ?.eq('rule_id', ruleId);

      // Insert new conditions
      if (conditions?.length > 0) {
        const snakeConditions = conditions?.map(cond => toSnakeCase({
          ...cond,
          ruleId
        }));

        const { error } = await supabase
          ?.from('automation_conditions')
          ?.insert(snakeConditions);

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error?.message);
            throw error;
          }
          console.log('Error updating conditions:', error?.message);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to update conditions:', error);
      throw error;
    }
  },

  async toggleRuleStatus(ruleId, isActive) {
    try {
      const { data, error } = await supabase
        ?.from('automation_rules')
        ?.update({ is_active: isActive })
        ?.eq('id', ruleId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error toggling rule status:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to toggle rule status:', error);
      throw error;
    }
  },

  async getExecutionLogs(ruleId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('automation_executions')
        ?.select(`
          *,
          recipients:automation_execution_recipients(
            id,
            recipient_email,
            recipient_name,
            email_sent,
            email_sent_at,
            email_opened,
            email_opened_at,
            email_clicked,
            email_clicked_at
          )
        `)
        ?.eq('rule_id', ruleId)
        ?.order('triggered_at', { ascending: false })
        ?.limit(limit);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching execution logs:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch execution logs:', error);
      throw error;
    }
  },

  async testRule(ruleId) {
    try {
      const rule = await this.getRuleById(ruleId);
      if (!rule) throw new Error('Rule not found');

      let matchedClients = [];
      let estimatedRecipients = 0;

      // Handle feedback-based triggers
      if (rule?.triggerType === 'feedback_rating_threshold' || rule?.triggerType === 'service_issue_detection') {
        const feedbackQuery = supabase
          ?.from('customer_feedback')
          ?.select(`
            *,
            work_order:work_orders(
              id,
              folio,
              client:clients(
                id,
                nombre,
                email,
                tipo_cliente
              )
            )
          `);

        // Apply conditions
        rule?.conditions?.forEach((condition, index) => {
          const field = condition?.fieldName;
          const operator = condition?.operator;
          const value = condition?.valueNumber || condition?.valueText || condition?.valueDate || condition?.valueBoolean;

          if (field === 'satisfaction_rating') {
            if (operator === 'less_than_or_equal') {
              feedbackQuery?.lte('satisfaction_rating', value);
            } else if (operator === 'less_than') {
              feedbackQuery?.lt('satisfaction_rating', value);
            } else if (operator === 'equals') {
              feedbackQuery?.eq('satisfaction_rating', value);
            } else if (operator === 'greater_than_or_equal') {
              feedbackQuery?.gte('satisfaction_rating', value);
            }
          }

          if (field === 'service_comments' && operator === 'contains') {
            // Keyword detection for service issues
            const keywords = value?.split(',')?.map(k => k?.trim());
            if (keywords?.length > 0) {
              feedbackQuery?.or(keywords?.map(k => `service_comments.ilike.%${k}%`)?.join(','));
            }
          }

          if (field === 'follow_up_requested') {
            feedbackQuery?.eq('follow_up_requested', value);
          }
        });

        const { data: feedbackData, error: feedbackError } = await feedbackQuery?.limit(10);

        if (feedbackError) {
          console.error('Error testing feedback rule:', feedbackError);
          throw feedbackError;
        }

        // Extract unique clients from feedback
        const clientMap = new Map();
        feedbackData?.forEach(feedback => {
          const client = feedback?.work_order?.client;
          if (client && !clientMap?.has(client?.id)) {
            clientMap?.set(client?.id, {
              id: client?.id,
              nombre: client?.nombre,
              email: client?.email,
              tipoCliente: client?.tipo_cliente
            });
          }
        });

        matchedClients = Array.from(clientMap?.values());
        estimatedRecipients = matchedClients?.length;

        return {
          matchedClients,
          estimatedRecipients,
          conditions: rule?.conditions
        };
      }

      // Existing logic for other trigger types
      const { data: clients, error } = await supabase
        ?.from('clients')
        ?.select('id, nombre, email, tipo_cliente')
        ?.limit(10);

      if (error) {
        console.error('Error testing rule:', error);
        throw error;
      }

      matchedClients = toCamelCase(clients || []);
      estimatedRecipients = matchedClients?.length;

      return {
        matchedClients,
        estimatedRecipients,
        conditions: rule?.conditions
      };
    } catch (error) {
      console.error('Failed to test rule:', error);
      throw error;
    }
  },

  async getSampleClientsForRule(rule) {
    try {
      // Build query based on conditions
      let query = supabase
        ?.from('clients')
        ?.select(`
          id,
          nombre,
          email,
          tipo_cliente
        `)
        ?.limit(10);

      // Apply basic filters based on trigger type
      if (rule?.triggerType === 'client_revenue_threshold' || rule?.triggerType === 'client_lifetime_value') {
        // Get clients with work orders
        const { data: clientsWithOrders } = await supabase
          ?.from('work_orders')
          ?.select('client_id, total')
          ?.eq('estado_ot', 'entregado');

        const clientRevenue = {};
        clientsWithOrders?.forEach(order => {
          const clientId = order?.client_id;
          if (!clientRevenue?.[clientId]) clientRevenue[clientId] = 0;
          clientRevenue[clientId] += parseFloat(order?.total || 0);
        });

        // Filter by revenue threshold from conditions
        const revenueCondition = rule?.conditions?.find(c => 
          c?.fieldName === 'client_lifetime_value' || c?.fieldName === 'total_revenue'
        );
        
        if (revenueCondition) {
          const threshold = parseFloat(revenueCondition?.valueNumber || 0);
          const qualifiedClientIds = Object.entries(clientRevenue)
            ?.filter(([_, revenue]) => revenue >= threshold)
            ?.map(([clientId]) => clientId)
            ?.slice(0, 10);

          if (qualifiedClientIds?.length > 0) {
            query = query?.in('id', qualifiedClientIds);
          }
        }
      }

      const { data, error } = await query;

      if (error) {
        console.log('Error fetching sample clients:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch sample clients:', error);
      return [];
    }
  },

  async getRuleVersions(ruleId) {
    try {
      const { data, error } = await supabase
        ?.from('automation_rule_versions')
        ?.select(`
          *,
          changed_by_user:user_profiles!automation_rule_versions_changed_by_fkey(
            id,
            full_name,
            email
          )
        `)
        ?.eq('rule_id', ruleId)
        ?.order('version_number', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching rule versions:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch rule versions:', error);
      throw error;
    }
  },

  async getExecutionStatistics(ruleId) {
    try {
      const { data, error } = await supabase
        ?.from('automation_executions')
        ?.select('execution_status, matched_clients_count, emails_sent_count')
        ?.eq('rule_id', ruleId);

      if (error) {
        console.log('Error fetching execution statistics:', error?.message);
        return {
          totalExecutions: 0,
          successfulExecutions: 0,
          failedExecutions: 0,
          totalEmailsSent: 0,
          totalClientsMatched: 0
        };
      }

      const stats = data?.reduce((acc, exec) => {
        acc.totalExecutions += 1;
        if (exec?.execution_status === 'completed') acc.successfulExecutions += 1;
        if (exec?.execution_status === 'failed') acc.failedExecutions += 1;
        acc.totalEmailsSent += exec?.emails_sent_count || 0;
        acc.totalClientsMatched += exec?.matched_clients_count || 0;
        return acc;
      }, {
        totalExecutions: 0,
        successfulExecutions: 0,
        failedExecutions: 0,
        totalEmailsSent: 0,
        totalClientsMatched: 0
      });

      return stats;
    } catch (error) {
      console.error('Failed to fetch execution statistics:', error);
      return {
        totalExecutions: 0,
        successfulExecutions: 0,
        failedExecutions: 0,
        totalEmailsSent: 0,
        totalClientsMatched: 0
      };
    }
  },

  // Get feedback template suggestions for automation rules
  async getFeedbackTemplateOptions() {
    try {
      const { data, error } = await supabase
        ?.from('email_templates')
        ?.select('id, template_name, segment, subject_line, metadata')
        ?.in('segment', ['feedback_low_rating', 'feedback_critical_issue', 'feedback_positive'])
        ?.eq('status', 'active')
        ?.order('template_name');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching feedback templates:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch feedback templates:', error);
      return [];
    }
  },

  // Create automation rule for feedback-based email triggers
  async createFeedbackAutomationRule(ruleData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      // Validate feedback-specific trigger types
      const validFeedbackTriggers = [
        'feedback_rating_threshold',
        'service_issue_detection'
      ];

      if (!validFeedbackTriggers?.includes(ruleData?.triggerType)) {
        throw new Error('Invalid feedback trigger type');
      }

      const snakeData = toSnakeCase({
        ...ruleData,
        createdBy: user?.id,
        isActive: true,
        status: 'active'
      });

      const { data, error } = await supabase
        ?.from('automation_rules')
        ?.insert(snakeData)
        ?.select(`
          *,
          email_template:email_templates(
            id,
            template_name,
            segment
          )
        `)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error creating feedback automation rule:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create feedback automation rule:', error);
      throw error;
    }
  },

  // Get feedback automation rule suggestions
  getFeedbackRuleSuggestions() {
    return [
      {
        id: 'suggestion-low-rating',
        ruleName: 'Auto-send Low Rating Follow-up',
        triggerType: 'feedback_rating_threshold',
        description: 'Automatically send follow-up email when satisfaction rating is ≤3 stars',
        suggestedConditions: [
          {
            fieldName: 'satisfaction_rating',
            operator: 'less_than_or_equal',
            valueNumber: 3,
            logicalOperator: 'AND'
          }
        ],
        suggestedTemplate: 'feedback_low_rating',
        priority: 'high'
      },
      {
        id: 'suggestion-critical-issue',
        ruleName: 'Critical Issue Alert',
        triggerType: 'service_issue_detection',
        description: 'Send urgent alert when critical keywords detected in feedback comments',
        suggestedConditions: [
          {
            fieldName: 'service_comments',
            operator: 'contains',
            valueText: 'problema|falla|queja|insatisfecho|deficiente',
            logicalOperator: 'OR'
          },
          {
            fieldName: 'satisfaction_rating',
            operator: 'less_than_or_equal',
            valueNumber: 2,
            logicalOperator: 'AND'
          }
        ],
        suggestedTemplate: 'feedback_critical_issue',
        priority: 'critical'
      },
      {
        id: 'suggestion-positive-followup',
        ruleName: 'Thank You for Positive Feedback',
        triggerType: 'feedback_rating_threshold',
        description: 'Send appreciation email for high satisfaction ratings (4-5 stars)',
        suggestedConditions: [
          {
            fieldName: 'satisfaction_rating',
            operator: 'greater_than_or_equal',
            valueNumber: 4,
            logicalOperator: 'AND'
          }
        ],
        suggestedTemplate: 'feedback_positive',
        priority: 'medium'
      }
    ];
  }
};